import { useEffect, useMemo, useState } from "react";
import {
  User,
  Search,
  ShoppingBag,
  Mail,
  ArrowRight,
  RefreshCw,
  Copy,
  Download,
  ExternalLink,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};
const fmtMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const CustomerRow = ({ customer, onOpenAssign }) => {
  const [expanded, setExpanded] = useState(false);
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadOrders = async () => {
    if (orders) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/private-customers/${encodeURIComponent(customer.customer_email)}/orders`
      );
      setOrders(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) loadOrders();
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(customer.customer_email);
      toast.success("Email copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const downloadPdf = async (order) => {
    try {
      const resp = await api.get(`/admin/orders/${order.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${order.invoice_number || order.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed");
    }
  };

  return (
    <li
      data-testid={`private-customer-row-${customer.customer_email}`}
      className="rounded-2xl bg-white border border-black/5 p-4 sm:p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display font-black text-[16px] text-brand-charcoal tracking-tight truncate">
              {customer.customer_name || customer.customer_email}
            </p>
            {customer.organization && (
              <span className="inline-flex items-center rounded-full bg-brand-charcoal/8 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/70">
                {customer.organization}
              </span>
            )}
          </div>
          <p className="mt-0.5 text-[12.5px] text-brand-charcoal/60 truncate">
            {customer.customer_email}
            {customer.customer_phone && ` · ${customer.customer_phone}`}
          </p>
          <p className="mt-1 text-[11.5px] text-brand-charcoal/55">
            <b className="text-brand-charcoal">{customer.orders}</b> order{customer.orders === 1 ? "" : "s"}
            {" · "}Lifetime{" "}
            <b className="text-brand-charcoal tabular-nums">{fmtMoney(customer.lifetime_value)}</b>
            {" · "}Last order {fmtDate(customer.last_order_at)}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          <button
            type="button"
            onClick={copyEmail}
            data-testid={`private-customer-copy-email-${customer.customer_email}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 text-brand-charcoal px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-[0.1em] hover:border-brand-teal hover:text-brand-teal"
          >
            <Copy className="h-3 w-3" />
            Copy email
          </button>
          <a
            href={`mailto:${customer.customer_email}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 text-brand-charcoal px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-[0.1em] hover:border-brand-teal hover:text-brand-teal"
          >
            <Mail className="h-3 w-3" />
            Email
          </a>
          <button
            type="button"
            onClick={toggle}
            data-testid={`private-customer-view-orders-${customer.customer_email}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-3.5 py-1.5 text-[10.5px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal"
          >
            {expanded ? "Hide" : "Orders"}
            <ChevronDown className={`h-3 w-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>

      {expanded && (
        <div
          data-testid={`private-customer-orders-${customer.customer_email}`}
          className="mt-4 rounded-2xl bg-brand-cream/40 border border-black/5 p-3 sm:p-4"
        >
          {loading && <p className="text-[12.5px] text-brand-charcoal/55">Loading…</p>}
          {!loading && orders && orders.length === 0 && (
            <p className="text-[12.5px] text-brand-charcoal/55">No orders on file.</p>
          )}
          {!loading && orders && orders.length > 0 && (
            <ul className="space-y-2">
              {orders.map((o) => (
                <li
                  key={o.id}
                  data-testid={`private-customer-order-${o.id}`}
                  className="rounded-xl bg-white border border-black/5 p-3 flex flex-wrap items-start justify-between gap-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center rounded-full bg-brand-teal/10 text-brand-teal px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">
                        {o.type || "order"}
                      </span>
                      <p className="font-bold text-[13px] text-brand-charcoal tabular-nums">
                        {o.invoice_number || o.id.slice(0, 8)}
                      </p>
                      <span className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand-charcoal/50">
                        {o.status || "—"}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[11.5px] text-brand-charcoal/55">
                      Created {fmtDate(o.created_at)}
                      {o.event_date ? ` · Event ${fmtDate(o.event_date)}` : ""}
                      {o.items_count ? ` · ${o.items_count} item${o.items_count === 1 ? "" : "s"}` : ""}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <p className="font-display font-black text-[15px] text-brand-charcoal tabular-nums">
                      {fmtMoney(o.total)}
                    </p>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => downloadPdf(o)}
                        className="inline-flex items-center gap-1 rounded-full bg-brand-charcoal text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] hover:bg-brand-teal"
                      >
                        <Download className="h-3 w-3" />
                        PDF
                      </button>
                      <a
                        href={`/admin/orders?highlight=${encodeURIComponent(o.id)}`}
                        className="inline-flex items-center gap-1 rounded-full bg-white border border-brand-teal/40 text-brand-teal px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] hover:bg-brand-teal hover:text-white"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Edit
                      </a>
                      <button
                        type="button"
                        onClick={() => onOpenAssign?.(o)}
                        className="inline-flex items-center gap-1 rounded-full bg-white border border-brand-gold/40 text-brand-gold-dark px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] hover:bg-brand-gold hover:text-brand-charcoal"
                      >
                        <ArrowRight className="h-3 w-3" />
                        To agency
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
};

const PrivateCustomersTab = ({ onOpenAssign }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/private-customers");
      setCustomers(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return customers;
    return customers.filter((c) =>
      [c.customer_email, c.customer_name, c.customer_phone, c.organization]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term))
    );
  }, [customers, q]);

  const totalLTV = useMemo(
    () => customers.reduce((s, c) => s + (Number(c.lifetime_value) || 0), 0),
    [customers]
  );

  return (
    <div data-testid="admin-private-customers-tab" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display font-black text-[20px] text-brand-charcoal tracking-tight">
            Private Customers
          </h3>
          <p className="mt-1 text-[12.5px] text-brand-charcoal/60 max-w-2xl">
            Every non-agency customer who has ever placed an order — grouped by
            email. Expand a row to see their order history, download PDFs, or
            attach an order to an agency profile.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="private-customers-refresh"
          className="inline-flex items-center gap-1.5 rounded-full border border-black/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-brand-charcoal hover:bg-brand-cream"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-charcoal/40" />
          <input
            type="text"
            data-testid="private-customers-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email, name, phone…"
            className="w-full h-11 rounded-xl bg-white border border-black/10 pl-10 pr-3 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
          />
        </div>
        <div className="text-[11.5px] text-brand-charcoal/60 flex items-center gap-3">
          <span>
            <b className="text-brand-charcoal">{filtered.length}</b>{" "}
            {filtered.length === 1 ? "customer" : "customers"}
          </span>
          <span>
            Total LTV: <b className="text-brand-charcoal tabular-nums">{fmtMoney(totalLTV)}</b>
          </span>
        </div>
      </div>

      {loading && <p className="text-[13px] text-brand-charcoal/55">Loading…</p>}

      {!loading && filtered.length === 0 && (
        <div className="rounded-3xl border border-black/5 bg-brand-cream/60 p-8 text-center">
          <User className="h-8 w-8 mx-auto text-brand-charcoal/40" />
          <p className="mt-3 font-display font-black text-[16px] text-brand-charcoal">
            {customers.length === 0 ? "No private customers yet" : "No customers match your search"}
          </p>
          {customers.length === 0 && (
            <p className="mt-1.5 text-[12.5px] text-brand-charcoal/55">
              Non-agency Stripe purchases and public Pay-Link invoices will
              show up here once they roll in.
            </p>
          )}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <ul className="space-y-2" data-testid="private-customers-list">
          {filtered.map((c) => (
            <CustomerRow
              key={c.customer_email}
              customer={c}
              onOpenAssign={onOpenAssign}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default PrivateCustomersTab;
